<script setup lang="ts">
import { useDronecastStore } from '@/stores/dronecastStore'
import { useLocalInput } from '@/composables/useLocalStorage'
import { IDCMessage, DCPayloadCastroomStatusChanged, DCPayloadCastingStartResult, DCPayloadCastroomCreateResult, DCPayloadIceCandidate, DCPayloadRtspCasterInfo } from '@dronecast/shared'
import { DronecastClient } from '@/services/DronecastClient'
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useRoute } from 'vue-router'
import { kurentoUtils } from '@/utils/kurentoUtils'
import { CastroomId, createCastroomId, castroomIdToKey } from '@dronecast/shared'
import type { MediaServerType } from '@dronecast/shared'

const route = useRoute()
const store = useDronecastStore()
const gcsId = useLocalInput('dronecast.gcsId', route.query.gcsId as string || 'KETI_GCS')

const websocketUrl = computed(() => store.websocketUrl)

const dronecastClientMap = new Map<string, DronecastClient>()
const webRtcPeerMap = new Map<string, any>()

const RTSP_STREAM_TABLE_SIZE = 40

/**
 * RTSP 소스 프리셋 — 공개 CCTV와 자체 MediaMTX 테스트 소스를 코드/UI에서 언제든 전환.
 * 미생성 슬롯의 기본 RTSP URL만 결정한다(이미 송출 중인 행의 URL은 유지).
 * CCTV 목록은 백업 겸 참고용으로 코드에 보존한다.
 */
type RtspSourcePreset = 'local' | 'cctv'
/** 서버 MediaMTX에 존재하는 자체영상 개수(cam01~camNN). 인덱스가 초과하면 cam01부터 순환한다. */
const LOCAL_CAM_COUNT = 13
const RTSP_SOURCE_PRESETS: Record<RtspSourcePreset, { label: string; url: (index: number) => string }> = {
  // 자체영상: 서버 로컬 MediaMTX(cam01~camNN). 동시연결 제한 없음. cam 개수 초과 시 순환(cam01부터).
  local: {
    label: `자체영상 (MediaMTX cam01~${LOCAL_CAM_COUNT.toString().padStart(2, '0')})`,
    url: (i) => `rtsp://127.0.0.1:8554/cam${((i % LOCAL_CAM_COUNT) + 1).toString().padStart(2, '0')}`,
  },
  // 공개 CCTV: 국가교통정보센터(cctv040~). IP당 동시연결 제한이 있어 다수 동시 송출 시 실패 가능(백업/참고용).
  cctv: {
    label: '공개 CCTV (210.99.70.120)',
    url: (i) => `rtsp://210.99.70.120:1935/live/cctv0${40 + i}.stream?transport=tcp`,
  },
}
/** 현재 선택된 RTSP 소스 프리셋 (localStorage 유지, UI 셀렉트로 전환) */
const rtspSource = useLocalInput('dronecast.rtspSource', 'local')
const activePreset = computed(() => RTSP_SOURCE_PRESETS[rtspSource.value as RtspSourcePreset] ?? RTSP_SOURCE_PRESETS.local)

/** 단계별 진행 상태 */
type CastingStep = '-' | 'connecting' | 'creating' | 'negotiating' | 'ice_checking' | 'casting' | 'error' | 'stopped'

/** 행 재생성(=focus 손실) 방지를 위한 안정적 key 생성기 */
let uidSeq = 0
const nextUid = () => `u${++uidSeq}`

interface CameraStream {
  uid: string
  cameraName: string
  rtspUrl: string
  status: string       // 서버 캐스트룸 상태 (ready, casting, closed)
  step: CastingStep    // 클라이언트 측 단계
  stepDetail?: string  // 추가 상세 (ICE state 등)
  recording?: boolean
  linkStatus?: string
  mediaServerType: MediaServerType
}

interface DroneGroup {
  uid: string
  droneName: string
  cameras: CameraStream[]
}

const droneGroups = ref<DroneGroup[]>([])

const stepLabel = (cam: CameraStream): string => {
  switch (cam.step) {
    case '-': return cam.status === '-' ? '-' : cam.status.toUpperCase()
    case 'connecting': return 'CONNECTING'
    case 'creating': return 'CREATING'
    case 'negotiating': return 'NEGOTIATING'
    case 'ice_checking': return 'ICE_CHECK'
    case 'casting': return 'CASTING'
    case 'error': return 'ERROR'
    case 'stopped': return 'STOPPED'
    default: return cam.status?.toUpperCase() || '-'
  }
}

const stepColor = (cam: CameraStream): string => {
  switch (cam.step) {
    case 'casting': return '#28a745'
    case 'error': return '#dc3545'
    case 'stopped': return '#6c757d'
    case 'connecting': case 'creating': case 'negotiating': case 'ice_checking': return '#ffc107'
    default: return '#aaa'
  }
}

const findCamera = (droneName: string, cameraName: string): CameraStream | undefined => {
  const group = droneGroups.value.find(g => g.droneName === droneName)
  return group?.cameras.find(c => c.cameraName === cameraName)
}

const getDronecastClient = (castroomId: CastroomId) => {
  const key = castroomIdToKey(castroomId)
  let dronecastClient = dronecastClientMap.get(key)
  if (!dronecastClient) {
    dronecastClient = new DronecastClient('caster')
    dronecastClient.connect(websocketUrl.value)

    dronecastClient.setMessageHandler('create_castroom_result', handleCreateCastroomResult)
    dronecastClient.setMessageHandler('exchange_icecandidate', handleExchangeIcecandidate)
    dronecastClient.setMessageHandler('cast_stream_result', handleCastStreamResult)
    dronecastClient.setMessageHandler('castroom_status_changed', handleCastroomStatusChanged)
    dronecastClient.setMessageHandler('caster_link_status_changed', handleCasterLinkStatusChanged)
    dronecastClient.setMessageHandler('evt.rtsp_caster.status', handleRtspCasterStatus)

    dronecastClientMap.set(key, dronecastClient)
  }

  return dronecastClient
}

const clearDronecastClient = (castroomId: CastroomId) => {
  const key = castroomIdToKey(castroomId)
  const dronecastClient = dronecastClientMap.get(key)
  if (dronecastClient) {
    dronecastClient.removeHandler('create_castroom_result')
    dronecastClient.removeHandler('exchange_icecandidate')
    dronecastClient.removeHandler('cast_stream_result')
    dronecastClient.removeHandler('castroom_status_changed')
    dronecastClient.removeHandler('caster_link_status_changed')
    dronecastClient.removeHandler('evt.rtsp_caster.status')

    dronecastClientMap.delete(key)
  }
}


const createCastroom = (droneName: string, cam: CameraStream) => {
  const { cameraName, rtspUrl } = cam
  if (!gcsId.value || !droneName || !cameraName || !rtspUrl) return

  cam.step = 'connecting'
  cam.stepDetail = undefined

  const castroomId = createCastroomId(gcsId.value, droneName, cameraName)
  const dronecastClient = getDronecastClient(castroomId)
  if (!dronecastClient) return

  // 서버측 RTSP 는 RTSP Caster(C-08) spawn 이 유일한 경로다.
  // 서버가 PlayerEndpoint 로 직접 RTSP 를 당기던 KMS 경로는 ADR-B25 로 사라졌다.
  cam.step = 'creating'
  dronecastClient.send({
    protocol: 'dronecast/1.0',
    type: 'create_rtsp_caster',
    payload: { castroomId, rtspUrl },
  } as any)
  dronecastClient.setMessageHandler('res.rtsp_caster.create', (message: IDCMessage) => {
    const payload = message.payload as any
    if (payload.result !== 'success') {
      cam.step = 'error'
      cam.stepDetail = payload.error || 'RTSP Caster 생성 실패'
    }
    // 성공 응답은 spawn 직후이며 아직 스트리밍 전 — 이후 evt.rtsp_caster.status로 실제 상태 반영
  })
}

const closeCastroom = (droneName: string, cam: CameraStream) => {
  const castroomId = createCastroomId(gcsId.value, droneName, cam.cameraName)

  // 이전 세션에서 시작됐거나 새로고침 후에는 dronecastClientMap에 client가 없다.
  // 그 경우에도 중지가 되도록 client를 새로 연결해 stop_rtsp_caster를 전송한다.
  // (서버 stop_rtsp_caster 핸들러는 payload의 castroomId로 대상을 찾으므로 연결과 무관하게 동작)
  const dronecastClient = getDronecastClient(castroomId)
  // 서버 응답을 기다린 후 WS 종료 (최대 3초 타임아웃)
  const cleanup = () => {
    dronecastClient.close()
    clearDronecastClient(castroomId)
  }
  const timeout = setTimeout(cleanup, 3000)
  dronecastClient.setMessageHandler('res.rtsp_caster.stop', () => {
    clearTimeout(timeout)
    dronecastClient.removeHandler('res.rtsp_caster.stop')
    cleanup()
  })
  dronecastClient.send({
    protocol: 'dronecast/1.0',
    type: 'stop_rtsp_caster',
    payload: { castroomId },
  } as any)
  cam.step = 'stopped'
  cam.stepDetail = undefined
  cam.status = ''
  cam.linkStatus = undefined
}


// 브라우저가 뷰어로 SDP 협상을 하던 경로(startCasting)는 KMS 전용이었다 —
// 서버측 RTSP 는 이제 RTSP Caster(C-08)가 협상한다. ADR-B25 로 제거.

const stopCasting = (castroomId: CastroomId) => {
  const cam = findCamera(castroomId.drone, castroomId.camera)
  if (!cam) return

  const key = castroomIdToKey(castroomId)
  const dronecastClient = dronecastClientMap.get(key)

  if (dronecastClient) {
    // BL-611: admin이 종료한 경우 viewer에게 사유 전달
    dronecastClient.closeCastroom(castroomId, 'admin_closed')
    setTimeout(() => {
      dronecastClient.close()
      clearDronecastClient(castroomId)
    }, 500)
  }

  const webRtcPeer = webRtcPeerMap.get(key)
  if (webRtcPeer) {
    try { kurentoUtils.dispose(webRtcPeer) } catch {}
    webRtcPeerMap.delete(key)
  }

  cam.step = 'stopped'
  cam.stepDetail = undefined
  cam.status = ''
  cam.linkStatus = undefined
}


const handleCreateCastroomResult = (message: IDCMessage) => {
  const { result, castroomId, error } = message.payload as DCPayloadCastroomCreateResult

  const cam = findCamera(castroomId.drone, castroomId.camera)

  if (result === 'error') {
    console.error('handleCreateCastroomResult error:', error)
    if (cam) {
      cam.step = 'error'
      cam.stepDetail = error || '캐스트룸 생성 실패'
    }
  } else {
    if (cam) cam.step = 'casting'
  }
}


const handleExchangeIcecandidate = (message: IDCMessage) => {
  const { castroomId, candidate } = message.payload as DCPayloadIceCandidate

  const key = castroomIdToKey(createCastroomId(castroomId.gcs, castroomId.drone, castroomId.camera))
  const webRtcPeer = webRtcPeerMap.get(key)
  if (webRtcPeer && webRtcPeer.peerConnection.localDescription) {
    webRtcPeer.addIceCandidate(candidate)
  }
}

const handleCastStreamResult = (message: IDCMessage) => {
  const { castroomId, result, data, error } = message.payload as DCPayloadCastingStartResult
  const cam = findCamera(castroomId.drone, castroomId.camera)

  if (result === 'error') {
    console.error('handleCastStreamResult error:', error)
    if (cam) {
      cam.step = 'error'
      cam.stepDetail = error || 'SDP 실패'
    }
  } else if (data) {
    if (cam) cam.step = 'ice_checking'

    const key = castroomIdToKey(createCastroomId(castroomId.gcs, castroomId.drone, castroomId.camera))
    const webRtcPeer = webRtcPeerMap.get(key)
    webRtcPeer?.processAnswer(data)
  }
}

const handleCastroomStatusChanged = (message: IDCMessage) => {
  const { castroomId, currentStatus } = message.payload as DCPayloadCastroomStatusChanged

  const cam = findCamera(castroomId.drone, castroomId.camera)
  if (cam) {
    cam.status = currentStatus
    if (currentStatus === 'casting' && cam.step !== 'casting') {
      cam.step = 'casting'
      cam.stepDetail = undefined
    } else if (currentStatus === 'closed') {
      cam.step = 'stopped'
      cam.stepDetail = '서버 종료'
    }
  }

  const sharedCastroomId = createCastroomId(castroomId.gcs, castroomId.drone, castroomId.camera)
  store.updateCastroomStatus(sharedCastroomId, currentStatus)
}

const handleCasterLinkStatusChanged = (message: IDCMessage) => {
  const payload = message.payload as any
  const { castroomId, casterLinkStatus } = payload
  const cam = findCamera(castroomId.drone, castroomId.camera)
  if (cam) {
    cam.linkStatus = casterLinkStatus
  }
}

/**
 * RTSP Caster 상태 이벤트 수신 — GStreamer 프로세스 라이프사이클을 UI에 반영
 * 백엔드 RtspCasterManager가 모든 상태 전이(크래시/재연결/종료)에서 브로드캐스트.
 */
const handleRtspCasterStatus = (message: IDCMessage) => {
  const info = message.payload as DCPayloadRtspCasterInfo
  const { castroomId, status, error, retryCount } = info
  const cam = findCamera(castroomId.drone, castroomId.camera)
  if (!cam) return

  switch (status) {
    case 'starting':
      cam.step = 'creating'
      cam.stepDetail = undefined
      break
    case 'connecting':
      cam.step = 'connecting'
      cam.stepDetail = undefined
      break
    case 'streaming':
      cam.step = 'casting'
      cam.stepDetail = undefined
      cam.status = 'casting'
      break
    case 'reconnecting':
      cam.step = 'error'
      cam.stepDetail = `재연결 중 (${retryCount ?? 0}회): ${error || '사유 불명'}`
      break
    case 'error':
      cam.step = 'error'
      cam.stepDetail = error || 'RTSP Caster 오류'
      break
    case 'stopped':
      cam.step = 'stopped'
      cam.stepDetail = undefined
      cam.status = ''
      break
  }
}

const linkStatusColor = (status?: string) => {
  switch (status) {
    case 'connected': return '#28a745'
    case 'unstable': return '#ffc107'
    case 'disconnected': return '#dc3545'
    default: return '#6c757d'
  }
}

const isCasting = (cam: CameraStream) => {
  return cam.step === 'casting' || cam.step === 'negotiating' || cam.step === 'ice_checking'
    || cam.step === 'creating' || cam.step === 'connecting'
    || cam.status === 'casting' || cam.status === 'ready'
}


onMounted(async () => {
  await updateDroneGroups()
})

onUnmounted(() => {
  for (const [, client] of dronecastClientMap) {
    try { client.close() } catch {}
  }
  dronecastClientMap.clear()
})


/** 플랫 스트림 목록을 드론별 그룹으로 변환 */
function groupByDrone(streams: { droneName: string, cam: CameraStream }[]): DroneGroup[] {
  const map = new Map<string, DroneGroup>()
  for (const { droneName, cam } of streams) {
    let group = map.get(droneName)
    if (!group) {
      group = { uid: nextUid(), droneName, cameras: [] }
      map.set(droneName, group)
    }
    group.cameras.push(cam)
  }
  return Array.from(map.values())
}

const updateDroneGroups = async () => {
  await store.fetchDronecastList(gcsId.value)

  const flatStreams: { droneName: string, cam: CameraStream }[] = []
  const serverKeys = new Set<string>()

  store.castroomList.forEach((castroom) => {
    const key = `${castroom.castingSource.droneName}:${castroom.castingSource.cameraName}`
    serverKeys.add(key)

    const step: CastingStep = castroom.status === 'casting' ? 'casting'
      : castroom.status === 'ready' ? 'creating'
      : '-'

    flatStreams.push({
      droneName: castroom.castingSource.droneName,
      cam: {
        uid: nextUid(),
        cameraName: castroom.castingSource.cameraName,
        rtspUrl: castroom.rtspUrl || '',
        status: castroom.status,
        step,
        mediaServerType: (castroom.mediaServerType as MediaServerType) || 'headless',
        linkStatus: castroom.casterLinkStatus,
      },
    })
  })

  const NUMBER_OF_CAMERAS = [3,2,2,2,3,4,3,4,1,1,1,1]
  let presetIndex = 0   // 선택된 프리셋의 0-based URL 인덱스
  let totalCount = flatStreams.length

  for (let d = 1; d <= NUMBER_OF_CAMERAS.length; d++) {
    const droneId = `KETI_Simul_${d}`

    const numberOfCameras = NUMBER_OF_CAMERAS[d - 1]
    for (let c = 1; c <= numberOfCameras; c++) {
      const cameraId = `camera${c.toString().padStart(2, '0')}`
      const rtspUrl = activePreset.value.url(presetIndex++)
      const key = `${droneId}:${cameraId}`

      if (serverKeys.has(key)) continue

      flatStreams.push({
        droneName: droneId,
        cam: {
          uid: nextUid(),
          cameraName: cameraId,
          rtspUrl,
          status: '-',
          step: '-',
          mediaServerType: 'headless',
        },
      })

      totalCount++
      if (totalCount >= RTSP_STREAM_TABLE_SIZE) break
    }
    if (totalCount >= RTSP_STREAM_TABLE_SIZE) break
  }

  droneGroups.value = groupByDrone(flatStreams)
}

function addDrone() {
  const existingNumbers = droneGroups.value
    .map(g => g.droneName.match(/^KETI_Simul_(\d+)$/)?.[1])
    .filter(Boolean)
    .map(Number)
  const next = existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1
  const droneName = `KETI_Simul_${next}`

  droneGroups.value.push({
    uid: nextUid(),
    droneName,
    cameras: [{
      uid: nextUid(),
      cameraName: 'camera01',
      rtspUrl: '',
      status: '-',
      step: '-',
      mediaServerType: 'headless',
    }],
  })
}

function addCamera(droneIndex: number) {
  const group = droneGroups.value[droneIndex]
  if (!group) return

  const existingNumbers = group.cameras
    .map(c => c.cameraName.match(/^camera(\d+)$/)?.[1])
    .filter(Boolean)
    .map(Number)
  const next = existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1
  const cameraName = `camera${next.toString().padStart(2, '0')}`

  group.cameras.push({
    uid: nextUid(),
    cameraName,
    rtspUrl: '',
    status: '-',
    step: '-',
    mediaServerType: 'headless',
  })
}

function removeCamera(droneIndex: number, camIndex: number) {
  const group = droneGroups.value[droneIndex]
  if (!group) return

  const cam = group.cameras[camIndex]
  if (cam && cam.step !== '-' && cam.step !== 'stopped') {
    closeCastroom(group.droneName, cam)
  }

  group.cameras.splice(camIndex, 1)

  // 카메라가 모두 삭제되면 드론 그룹도 제거
  if (group.cameras.length === 0) {
    droneGroups.value.splice(droneIndex, 1)
  }
}

function isValidRtspUrl(url: string): boolean {
  return /^rtsp:\/\/.+/.test(url)
}

function toggleRecording(cam: CameraStream) {
  cam.recording = !cam.recording
  // TODO: 서버 녹화 API 연동
}

async function closeCastrooms() {
  for (const group of droneGroups.value) {
    for (const cam of group.cameras) {
      const castroomId = createCastroomId(gcsId.value, group.droneName, cam.cameraName)
      stopCasting(castroomId)
    }
  }
}

</script>

<template>
  <main>
    <h1 class="page-title">다중 RTSP 캐스팅</h1>
    <div class="form-group horizontal">
      <label for="gcsId">GCS ID</label>
      <input id="gcsId" v-model="gcsId" class="stream-select" />
      <button class="btn btn-primary" @click="updateDroneGroups()">조회</button>
      <label for="rtspSrc">소스</label>
      <select id="rtspSrc" v-model="rtspSource" class="stream-select" @change="updateDroneGroups()">
        <option value="local">자체영상 (cam01~40)</option>
        <option value="cctv">공개 CCTV</option>
      </select>
      <button class="btn btn-secondary" @click="addDrone()">드론 추가</button>
      <button class="btn btn-danger" @click="closeCastrooms()">전체 중지</button>
    </div>

    <div class="rtsp-stream-table">
      <table>
        <thead>
          <tr>
            <th class="col-drone">드론</th>
            <th class="col-camera">카메라</th>
            <th class="col-url">RTSP URL</th>
            <th class="col-status">상태</th>
            <th class="col-action">Action</th>
            <th class="col-note">비고</th>
          </tr>
        </thead>
        <tbody v-for="(group, dIdx) in droneGroups" :key="group.uid">
          <tr
            v-for="(cam, cIdx) in group.cameras"
            :key="cam.uid"
            :class="{ 'row-casting': cam.step === 'casting', 'row-error': cam.step === 'error' }"
          >
            <!-- 드론 컬럼: 첫 카메라 행에만 표시 (rowspan) -->
            <td v-if="cIdx === 0" class="col-drone" :rowspan="group.cameras.length">
              <div class="drone-cell">
                <input v-model="group.droneName" type="text" placeholder="드론" :disabled="group.cameras.some(c => isCasting(c))" />
                <button class="btn btn-secondary btn-icon" @click="addCamera(dIdx)" title="카메라 추가">+</button>
              </div>
            </td>
            <!-- 카메라 -->
            <td class="col-camera">
              <input v-model="cam.cameraName" type="text" placeholder="카메라" :disabled="isCasting(cam)" />
            </td>
            <!-- RTSP URL -->
            <td class="col-url">
              <input v-model="cam.rtspUrl" type="text" placeholder="rtsp://..." :disabled="isCasting(cam)" :class="{ 'invalid-url': cam.rtspUrl && !isValidRtspUrl(cam.rtspUrl) }" />
            </td>
            <!-- 상태 (링크 + 상태값 통합) -->
            <td class="col-status">
              <span class="status-cell">
                <span class="link-dot" :style="{ background: linkStatusColor(cam.linkStatus) }"></span>
                <span class="link-arrow">→</span>
                <span class="link-dot" :style="{ background: cam.status === 'casting' ? '#28a745' : '#6c757d' }"></span>
                <span class="step-label" :style="{ color: stepColor(cam) }">{{ stepLabel(cam) }}</span>
              </span>
            </td>
            <!-- Action (미디어 라디오 + 송출 버튼 그룹) -->
            <td class="col-action">
              <div class="action-cell">
                <div class="action-btn-group">
                  <button v-if="!isCasting(cam)" class="btn btn-primary btn-sm" @click="createCastroom(group.droneName, cam)" :disabled="!isValidRtspUrl(cam.rtspUrl)">송출 시작</button>
                  <button v-else class="btn btn-danger btn-sm" @click="closeCastroom(group.droneName, cam)">송출 중지</button>
                </div>
                <button class="btn btn-sm" :class="cam.recording ? 'btn-danger' : 'btn-secondary'" @click="toggleRecording(cam)" :disabled="cam.step !== 'casting'">
                  {{ cam.recording ? '중지' : '녹화' }}
                </button>
                <button class="btn btn-secondary btn-sm" @click="removeCamera(dIdx, cIdx)" title="카메라 삭제">&times;</button>
              </div>
            </td>
            <!-- 비고 -->
            <td class="col-note">
              <span v-if="cam.step === 'error'" class="note-error">{{ cam.stepDetail || '' }}</span>
              <span v-else-if="cam.step === 'stopped' && cam.stepDetail">{{ cam.stepDetail }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </main>
</template>

<style lang="scss" scoped>
@use '@/assets/styles/variables' as vars;

main {
  padding: vars.$spacing-5;
  margin: 0 auto;
}

h1 {
  font-size: vars.$font-size-2xl;
  margin-bottom: vars.$spacing-4;
  text-align: center;
}

.rtsp-stream-table {
  width: 100%;

  table {
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
  }

  th, td {
    padding: 4px 6px;
    text-align: center;
    vertical-align: middle;
    border: 1px solid #444;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  input, select {
    width: 100%;
    box-sizing: border-box;
  }
}

.col-drone  { width: 240px; }
.col-camera { width: 90px; }
.col-url    { /* 나머지 공간 자동 */ }
.col-status { width: 150px; }
.col-action { width: 260px; }
.col-note   { width: 140px; }

.drone-cell {
  display: flex;
  align-items: center;
  gap: 4px;

  input {
    flex: 1;
    min-width: 0;
  }
}

.btn-icon {
  width: 22px;
  height: 22px;
  padding: 0;
  font-size: 14px;
  line-height: 22px;
  flex-shrink: 0;
}

.status-cell {
  display: inline-flex;
  align-items: center;
  gap: 3px;
}

.step-label {
  font-size: 0.8em;
  font-weight: 500;
  margin-left: 2px;
}

.link-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.link-arrow {
  color: #555;
  font-size: 0.7em;
}

.row-casting {
  background: rgba(40, 167, 69, 0.08);
}

.row-error {
  background: rgba(220, 53, 69, 0.08);
}

.action-cell {
  display: flex;
  gap: 3px;
  justify-content: center;
  align-items: center;
  flex-wrap: nowrap;
}

.action-btn-group {
  display: inline-flex;
  align-items: center;
  border: 1px solid #666;
  border-radius: 3px;
  overflow: hidden;
  gap: 0;

  .media-radio {
    display: inline-flex;
    align-items: center;
    gap: 1px;
    padding: 2px 4px;
    font-size: 0.8em;
    cursor: pointer;
    white-space: nowrap;
    border-right: 1px solid #666;

    input[type="radio"] {
      margin: 0;
      width: 12px;
      height: 12px;
    }
  }

  .btn {
    border-radius: 0;
    border: none;
    border-left: none;
  }
}

.note-error {
  color: #dc3545;
  font-size: 0.8em;
}

.invalid-url {
  border-color: #dc3545 !important;
  background-color: rgba(220, 53, 69, 0.1);
}

.btn-sm {
  padding: 2px 6px;
  font-size: 11px;
}

.btn-secondary {
  background-color: #e9ecef;
  color: #495057;
  border: none;
  cursor: pointer;
}

.btn-danger {
  background-color: #dc3545;
  color: white;
  border: none;
  cursor: pointer;
}
</style>
